import { Minimize2, Maximize2, X, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { useUploads } from '../../context/UploadContext';
import UploadItem from './UploadItem';
import Modal from '../common/Modal';
import { IconButton } from '../common/Button';

/**
 * Upload progress, centred and wide enough to read.
 *
 * It was a 400px panel in the corner, which was the wrong shape for the thing it now shows:
 * seven pipeline stages per file, each with a sentence of detail. Centred at 760px they fit
 * on one line apiece.
 *
 * Minimising is deliberately kept. Uploads survive navigation by design
 * (docs/01-frontend-architecture.md §6), and a modal that cannot be pushed aside while a
 * 200 MB file uploads stops somebody working for no reason. Minimised, it returns to a
 * corner chip that still reports progress and reopens on a click — the uploads themselves
 * are untouched either way.
 */
export default function UploadTray() {
  const { items, trayOpen, minimised, setMinimised, activeCount, retry, cancel, remove, closeTray, overallProgress } =
    useUploads();

  if (!trayOpen) return null;

  const done = items.filter((i) => i.status === 'done').length;
  const failed = items.filter((i) => i.status === 'failed').length;

  const summary =
    activeCount > 0
      ? `Uploading ${activeCount} item${activeCount === 1 ? '' : 's'}`
      : failed > 0
        ? `${done} uploaded, ${failed} failed`
        : `${done} upload${done === 1 ? '' : 's'} complete`;

  const icon =
    activeCount > 0 ? (
      <Loader2 size={17} className="spin" style={{ color: 'var(--ac)' }} />
    ) : failed > 0 ? (
      <AlertCircle size={17} style={{ color: 'var(--danger)' }} />
    ) : (
      <CheckCircle2 size={17} style={{ color: 'var(--success)' }} />
    );

  /* Minimised: a corner chip, so a long upload does not hold the screen. */
  if (minimised) {
    return (
      <section className="upload-tray" aria-label="Upload progress">
        <header className="upload-tray__head" onClick={() => setMinimised(false)}>
          {icon}
          <strong style={{ fontSize: 'var(--fs-cell)' }}>{summary}</strong>
          <div className="row" style={{ gap: 2, marginLeft: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <IconButton icon={Maximize2} label="Show upload details" size={15} onClick={() => setMinimised(false)} />
            <IconButton icon={X} label="Close upload progress" size={16} onClick={closeTray} />
          </div>
        </header>
        {activeCount > 0 && (
          <div className="progress" style={{ borderRadius: 0 }}>
            <div className="progress__bar" style={{ width: `${overallProgress}%` }} />
          </div>
        )}
      </section>
    );
  }

  return (
    <Modal
      open
      onClose={closeTray}
      width={760}
      title={summary}
      subtitle={
        activeCount > 0
          ? 'Each step below is reported by the server as it happens, not on a timer.'
          : 'Everything the server did to these files.'
      }
      icon={icon}
      // Closing by accident mid-upload would hide progress somebody is watching; the
      // minimise and close controls are both right there instead.
      closeOnScrim={false}
      footer={
        <>
          <button type="button" className="btn btn-g" onClick={() => setMinimised(true)}>
            <Minimize2 size={15} /> Minimise
          </button>
          <button type="button" className="btn btn-p" onClick={closeTray}>
            {activeCount > 0 ? 'Continue in background' : 'Done'}
          </button>
        </>
      }
    >
      {/* No scroll container of its own: .modal__body already scrolls inside the modal's
          max-height, and nesting a second one produces two scrollbars for one list. */}
      {items.map((item) => (
        <UploadItem key={item.id} item={item} onRetry={retry} onCancel={cancel} onRemove={remove} />
      ))}
    </Modal>
  );
}
